import { BASTION, CELLARS, DUNGEON } from '@somnhold/content';
import { DIRS, hexDist, hexLen, hexLine, hexRing, type Hex } from './hex.ts';
import { idiv, imax } from './math/fixed.ts';
import { deriveSeed, rngInt, rngRange, seedRng, type RngState } from './math/rng.ts';
import type { DungeonRoom, ExpArea, MapDef, SectorDef } from './types.ts';

const key = (h: Hex): string => `${h.q},${h.r}`;

/**
 * Lays out the bastion: gates evenly spaced on the rampart ring and a winding path of
 * 10–14 hexes from each gate outward. Deterministic for a given seed and sector count.
 */
export const createBastionMap = (seed: number, sectorCount: number): MapDef => {
  const rng = seedRng(seed ^ 0x5eed);
  const B = BASTION.radius;
  const ring = hexRing(B);
  const offset = rngInt(rng, ring.length);
  const sectors: SectorDef[] = [];
  const used = new Set<string>();
  let maxD = B;
  for (let i = 0; i < sectorCount; i++) {
    const gate = ring[(offset + idiv(i * ring.length, sectorCount)) % ring.length] as Hex;
    const path = growPath(rng, gate, B, used, rngRange(rng, BASTION.pathMin, BASTION.pathMax));
    for (const h of path) {
      used.add(key(h));
      const d = hexLen(h.q, h.r);
      if (d > maxD) maxD = d;
    }
    sectors.push({ index: i, gate, path: path.slice().reverse() });
  }
  return { radius: maxD + 3, bastion: B, sectors, kind: 'bastion' };
};

/** Path cells from just outside the gate outward (gate-adjacent first). */
const growPath = (rng: RngState, gate: Hex, B: number, used: Set<string>, length: number): Hex[] => {
  const out: Hex[] = [];
  let cur = gate;
  let lastDir = -1;
  for (let step = 0; step < length; step++) {
    const d0 = hexLen(cur.q, cur.r);
    const options: { h: Hex; dir: number; w: number }[] = [];
    for (let k = 0; k < 6; k++) {
      const dir = DIRS[k] as Hex;
      const h = { q: cur.q + dir.q, r: cur.r + dir.r };
      const d = hexLen(h.q, h.r);
      if (d <= B) continue;
      if (step === 0 && d !== B + 1) continue;
      if (used.has(key(h)) || out.some((o) => o.q === h.q && o.r === h.r)) continue;
      // Keep a one-cell gap from other paths and from our own earlier cells (no loops).
      let crowded = false;
      for (const dd of DIRS) {
        const nk = `${h.q + dd.q},${h.r + dd.r}`;
        if (used.has(nk)) crowded = true;
      }
      for (let j = 0; j < out.length - 1; j++) if (hexDist(out[j] as Hex, h) <= 1) crowded = true;
      if (crowded) continue;
      // Prefer moving outward, allow sideways bends, never back toward the rampart.
      let w = d > d0 ? 6 : d === d0 ? 3 : 0;
      if (d <= B + 1 && step > 0) w = 0;
      if (k === lastDir) w += 2;
      if (w > 0) options.push({ h, dir: k, w });
    }
    if (options.length === 0) break;
    let total = 0;
    for (const o of options) total += o.w;
    let roll = rngInt(rng, total);
    let pick = options[0] as { h: Hex; dir: number; w: number };
    for (const o of options) {
      if (roll < o.w) {
        pick = o;
        break;
      }
      roll -= o.w;
    }
    out.push(pick.h);
    cur = pick.h;
    lastDir = pick.dir;
  }
  return out;
};

/** Incursion map: an open clearing with the node in the middle and three approaches. */
export const createIncursionMap = (seed: number): MapDef => {
  const rng = seedRng(seed ^ 0x1c0);
  const inner = 4;
  const ring = hexRing(inner);
  const offset = rngInt(rng, ring.length);
  const used = new Set<string>();
  const sectors: SectorDef[] = [];
  let maxD = inner;
  for (let i = 0; i < 3; i++) {
    const gate = ring[(offset + idiv(i * ring.length, 3)) % ring.length] as Hex;
    const path = growPath(rng, gate, inner, used, rngRange(rng, 6, 8));
    for (const h of path) {
      used.add(key(h));
      const d = hexLen(h.q, h.r);
      if (d > maxD) maxD = d;
    }
    sectors.push({ index: i, gate, path: path.slice().reverse() });
  }
  return { radius: maxD + 3, bastion: inner, sectors, kind: 'incursion' };
};

/**
 * A round clearing of its own (the dragon of the Burn's, docs/UNICAS.md §4.2): `radius` hexes of open ground, no roads
 * and nothing in the middle. Beyond it, a ring the board draws as the burnt rim.
 */
export const createClearingMap = (radius: number): MapDef => ({ radius: radius + 2, bastion: radius, sectors: [], kind: 'incursion', open: true });

/** Opening order of the five possible sectors: spread around the ring as players join. */
export const SECTOR_SLOTS = [0, 2, 4, 1, 3];
export const MAX_SECTORS = 5;

/**
 * Campaign layout: all five sector slots are laid out once (so the bastion never moves when a
 * sector opens), indexed in opening order. Use `openSectors` to get the playable map.
 */
export const createCampaignLayout = (seed: number): MapDef => {
  const rng = seedRng(seed ^ 0x5eed);
  const B = BASTION.radius;
  const ring = hexRing(B);
  const offset = rngInt(rng, ring.length);
  const used = new Set<string>();
  const sectors: SectorDef[] = [];
  let maxD = B;
  for (let i = 0; i < MAX_SECTORS; i++) {
    const slot = SECTOR_SLOTS[i] as number;
    const gate = ring[(offset + idiv(slot * ring.length, MAX_SECTORS)) % ring.length] as Hex;
    const path = growPath(rng, gate, B, used, rngRange(rng, BASTION.pathMin, BASTION.pathMax));
    for (const h of path) {
      used.add(key(h));
      const d = hexLen(h.q, h.r);
      if (d > maxD) maxD = d;
    }
    sectors.push({ index: i, gate, path: path.slice().reverse() });
  }
  return { radius: maxD + 3, bastion: B, sectors, kind: 'bastion' };
};

export const openSectors = (layout: MapDef, count: number): MapDef => ({ ...layout, sectors: layout.sectors.slice(0, count) });

/**
 * The crypt: an entrance, three rooms and the Warden's hall in a winding chain, joined by straight
 * corridors along hex directions. Each corridor ends in a door right before the next room.
 */
export const createDungeonMap = (seed: number, roomCount: number = DUNGEON.rooms, roomRadii?: number[]): MapDef => {
  const rng = seedRng(seed ^ 0xd06e);
  const radii = [DUNGEON.entryRadius, ...Array.from({ length: roomCount }, (_, i) => roomRadii?.[i] ?? DUNGEON.roomRadius), DUNGEON.bossRadius];
  const rooms: DungeonRoom[] = [{ q: 0, r: 0, radius: radii[0] as number, kind: 'entry' }];
  const corridors: { from: Hex; dir: number; len: number }[] = [];
  let dir = rngInt(rng, 6);
  for (let i = 1; i < radii.length; i++) {
    const prev = rooms[i - 1] as DungeonRoom;
    const rad = radii[i] as number;
    const dist = prev.radius + rad + DUNGEON.corridor + 1;
    // Bend left, right or keep straight; never fold back onto an earlier room.
    const turns = [5, 1, 0];
    const start = rngInt(rng, 3);
    let placed = false;
    for (let k = 0; k < 3 && !placed; k++) {
      const d = (dir + (turns[(start + k) % 3] as number)) % 6;
      const step = DIRS[d] as Hex;
      const c = { q: prev.q + step.q * dist, r: prev.r + step.r * dist };
      if (rooms.some((o) => hexDist(o, c) < o.radius + rad + DUNGEON.corridor + 1)) continue;
      corridors.push({ from: { q: prev.q, r: prev.r }, dir: d, len: dist });
      rooms.push({ q: c.q, r: c.r, radius: rad, kind: i === radii.length - 1 ? 'boss' : 'room' });
      dir = d;
      placed = true;
    }
    if (!placed) {
      const step = DIRS[dir] as Hex;
      corridors.push({ from: { q: prev.q, r: prev.r }, dir, len: dist });
      rooms.push({ q: prev.q + step.q * dist, r: prev.r + step.r * dist, radius: rad, kind: i === radii.length - 1 ? 'boss' : 'room' });
    }
  }
  // Center the chain on the origin.
  let sq = 0;
  let sr = 0;
  for (const r of rooms) {
    sq += r.q;
    sr += r.r;
  }
  const oq = idiv(sq, rooms.length);
  const or = idiv(sr, rooms.length);
  for (const r of rooms) {
    r.q -= oq;
    r.r -= or;
  }
  const floor = new Map<string, Hex>();
  const add = (h: Hex): void => {
    floor.set(key(h), h);
  };
  for (const r of rooms) {
    add({ q: r.q, r: r.r });
    for (let k = 1; k <= r.radius; k++) for (const h of hexRing(k)) add({ q: r.q + h.q, r: r.r + h.r });
  }
  const doors: Hex[] = [];
  corridors.forEach((c, i) => {
    const a = rooms[i] as DungeonRoom;
    const b = rooms[i + 1] as DungeonRoom;
    const step = DIRS[c.dir] as Hex;
    for (let k = a.radius + 1; k < c.len - b.radius; k++) add({ q: c.from.q - oq + step.q * k, r: c.from.r - or + step.r * k });
    const d = c.len - b.radius - 1;
    doors.push({ q: c.from.q - oq + step.q * d, r: c.from.r - or + step.r * d });
  });
  const cells = [...floor.values()].sort((x, y) => x.r - y.r || x.q - y.q);
  let maxD = 0;
  for (const h of cells) {
    const d = hexLen(h.q, h.r);
    if (d > maxD) maxD = d;
  }
  return { radius: maxD + 2, bastion: 0, sectors: [], kind: 'dungeon', dungeon: { rooms, doors, floor: cells } };
};

/**
 * The deep crypt (level III): three floors, each its own chain (entrance, a fighting room, a puzzle room, a
 * guardian's hall), laid far apart on one map in a triangle around the origin. The party only ever sees one:
 * the stairs take it from one floor's hall to the next floor's entrance. The "door" into a lower floor's
 * entrance is a placeholder at that entrance's centre (nothing to draw there).
 */
export const createDeepCryptMap = (seed: number, floors: number, puzzles: DungeonRoom['puzzle'][]): MapDef => {
  const chains = Array.from({ length: floors }, (_, f) => createDungeonMap(deriveSeed(seed, 77 + f), 2, [3, 4]));
  let ext = 0;
  for (const c of chains) for (const h of c.dungeon?.floor ?? []) ext = imax(ext, hexLen(h.q, h.r));
  const S = ext + 3;
  const rooms: DungeonRoom[] = [];
  const doors: Hex[] = [];
  const floor: Hex[] = [];
  chains.forEach((c, f) => {
    const L = c.dungeon;
    if (!L) return;
    const dir = DIRS[(f * 2) % 6] as Hex;
    const oq = dir.q * S;
    const or = dir.r * S;
    L.rooms.forEach((r, i) => {
      rooms.push({ ...r, q: r.q + oq, r: r.r + or, floor: f, ...(i === 2 ? { puzzle: puzzles[f] } : {}) });
      if (i === 0 && f > 0) doors.push({ q: r.q + oq, r: r.r + or });
    });
    for (const d of L.doors) doors.push({ q: d.q + oq, r: d.r + or });
    for (const h of L.floor) floor.push({ q: h.q + oq, r: h.r + or });
  });
  floor.sort((x, y) => x.r - y.r || x.q - y.q);
  let maxD = 0;
  for (const h of floor) maxD = imax(maxD, hexLen(h.q, h.r));
  return { radius: maxD + 2, bastion: 0, sectors: [], kind: 'dungeon', dungeon: { rooms, doors, floor } };
};

/** The garrison cellars' doors: from the way down to the floodgate, each wing in two rooms, and the floodgate. */
export const CELLAR_LINKS: [ExpArea, ExpArea][] = [
  ['entry', 'guard'],
  ['guard', 'hub'],
  ['hub', 'cisternA'],
  ['cisternA', 'cisternB'],
  ['hub', 'towerA'],
  ['towerA', 'towerB'],
  ['hub', 'powderA'],
  ['powderA', 'powderB'],
  ['hub', 'cells'],
  ['hub', 'cistern'],
];

/**
 * The garrison cellars (docs/EXPEDICIONES.md), laid out by hand: rooms where `CELLARS.rooms` puts them, joined by
 * straight corridors; each corridor's door is its last cell before the room it leads into. With three heroes
 * there are no cells (that wing's room and door are left out; its lock comes open).
 */
export const createCellarsMap = (heroes: number): { map: MapDef; areas: ExpArea[]; links: [ExpArea, ExpArea][] } => {
  const areas = (Object.keys(CELLARS.rooms) as ExpArea[]).filter((a) => a !== 'cells' || heroes >= 4);
  const kindOf = (a: ExpArea): DungeonRoom['kind'] => (a === 'entry' ? 'entry' : a === 'cistern' ? 'boss' : 'room');
  const rooms: DungeonRoom[] = areas.map((a) => ({ ...CELLARS.rooms[a], kind: kindOf(a) }));
  const floor = new Map<string, Hex>();
  const inRoom = (h: Hex): boolean => rooms.some((r) => hexDist(h, r) <= r.radius);
  for (const r of rooms) {
    floor.set(key(r), { q: r.q, r: r.r });
    for (let k = 1; k <= r.radius; k++) for (const h of hexRing(k)) floor.set(key({ q: r.q + h.q, r: r.r + h.r }), { q: r.q + h.q, r: r.r + h.r });
  }
  const links = CELLAR_LINKS.filter(([a, b]) => areas.includes(a) && areas.includes(b));
  const doors: Hex[] = [];
  for (const [a, b] of links) {
    const A = CELLARS.rooms[a];
    const B = CELLARS.rooms[b];
    const cells = hexLine(A, B).filter((h) => !inRoom(h));
    for (const h of cells) floor.set(key(h), h);
    doors.push(cells[cells.length - 1] ?? { q: B.q, r: B.r });
  }
  const cells = [...floor.values()].sort((x, y) => x.r - y.r || x.q - y.q);
  let maxD = 0;
  for (const h of cells) maxD = imax(maxD, hexLen(h.q, h.r));
  return { map: { radius: maxD + 2, bastion: 0, sectors: [], kind: 'dungeon', dungeon: { rooms, doors, floor: cells } }, areas, links };
};
